import { useEffect, useState, type CSSProperties } from "react";
import { useGame } from "@game/game-context";
import type { Me, OwnedDigimon, PositionResponse, Wallet } from "@game/types";

const wrapStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  background: "#0b0d12",
  color: "#e6e8ee",
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
  zIndex: 5000,
};

const barOuter: CSSProperties = {
  width: 360,
  height: 8,
  background: "rgba(255,255,255,0.08)",
  borderRadius: 4,
  overflow: "hidden",
  marginTop: 16,
};

const barInner = (frac: number): CSSProperties => ({
  width: `${Math.round(frac * 100)}%`,
  height: "100%",
  background: "linear-gradient(90deg, #4cb1ff, #a855f7)",
  transition: "width 200ms linear",
});

type Step =
  | "starting"
  | "health"
  | "data"
  | "session"
  | "profile"
  | "position"
  | "domain"
  | "ready"
  | "failed";

interface FailState {
  step: Step;
  message: string;
}

const STEP_LABEL: Record<Step, string> = {
  starting: "Starting…",
  health: "Reaching server…",
  data: "Loading game data…",
  session: "Restoring session…",
  profile: "Loading profile…",
  position: "Locating in world…",
  domain: "Loading inventory & quests…",
  ready: "Ready",
  failed: "Failed",
};

const STEP_PROGRESS: Record<Step, number> = {
  starting: 0.05,
  health: 0.15,
  data: 0.4,
  session: 0.55,
  profile: 0.7,
  position: 0.8,
  domain: 0.95,
  ready: 1,
  failed: 1,
};

export function BootScene() {
  const game = useGame();
  const [step, setStep] = useState<Step>("starting");
  const [error, setError] = useState<FailState | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // 1) Health
        setStep("health");
        const ok = await game.client.healthCheck();
        if (cancelled) return;
        if (!ok) {
          setStep("failed");
          setError({ step: "health", message: "Backend not reachable. Try again." });
          return;
        }

        // 2) Static data
        setStep("data");
        await game.client.data.load();
        if (cancelled) return;

        // 3) Session — try restored token; if absent or invalid, send to login
        setStep("session");
        if (!game.client.auth.isAuthenticated()) {
          game.setStage({ kind: "login" });
          return;
        }

        // 4) Profile
        setStep("profile");
        const me = await game.client.api.get<Me>("/me");
        if (cancelled) return;

        // Check ban
        if (me.bannedUntil && new Date(me.bannedUntil).getTime() > Date.now()) {
          setStep("failed");
          setError({
            step: "session",
            message: `Account suspended until ${new Date(me.bannedUntil).toLocaleString()}.`,
          });
          return;
        }

        // 5) Onboarding gate
        if (!me.onboardingComplete) {
          game.setStage({ kind: "onboarding", characterId: me.id });
          return;
        }

        // 6) Position
        setStep("position");
        const pos = await game.client.api.get<PositionResponse>("/world/position");
        if (cancelled) return;

        // 7) Parallel domain loads
        setStep("domain");
        const [digimonsRes, walletRes] = await Promise.all([
          game.client.api.get<{ digimons: OwnedDigimon[] }>("/digimons"),
          game.client.api.get<Wallet>("/wallet"),
        ]);
        if (cancelled) return;

        setStep("ready");
        game.setStage({
          kind: "world",
          session: {
            me,
            position: pos.position,
            digimons: digimonsRes.digimons,
            wallet: walletRes,
          },
        });
      } catch (err) {
        if (cancelled) return;
        const msg = (err as Error).message ?? "Unknown error";
        const code = (err as { code?: string }).code;
        // 401 → re-login
        if (code === "http_401" || code === "invalid_token") {
          game.setStage({ kind: "login" });
          return;
        }
        setStep("failed");
        setError({ step: "starting", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [game]);

  const frac = STEP_PROGRESS[step];

  return (
    <div style={wrapStyle}>
      <div style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Digital Frontier</div>
      <div style={{ opacity: 0.65, fontSize: 13 }}>{STEP_LABEL[step]}</div>
      <div style={barOuter}>
        <div style={barInner(frac)} />
      </div>
      {error && (
        <div style={{ marginTop: 24, color: "#ef4444", fontSize: 13, textAlign: "center", maxWidth: 420 }}>
          {error.message}
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                background: "#2563eb",
                color: "#fff",
                border: "none",
                padding: "6px 14px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              Retry
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
